<script setup lang="ts">
import { company } from '~/data/company'

const { plausibleHost } = useRuntimeConfig().public
const hasSocial = Object.values(company.social).some(Boolean)
</script>

<template>
  <div>
    <p>
      Stand: Oktober 2026. Diese Datenschutzerklärung informiert Sie darüber, welche personenbezogenen Daten wir beim Besuch dieser Website verarbeiten.
    </p>

    <h2>Verantwortlicher</h2>
    <p>
      {{ company.owner }}, {{ company.name }}<br>
      {{ company.street }}, {{ company.zip }} {{ company.city }}<br>
      Telefon: {{ company.phone }}<br>
      E-Mail: <a :href="`mailto:${company.email}`">{{ company.email }}</a>
    </p>
    <h2>Hosting und Server-Logfiles</h2>
    <p>
      Diese Website wird als statische Website bei
      <template v-if="company.hosting.name">
        {{ company.hosting.name }}, {{ company.hosting.address }},
      </template>
      <template v-else>
        unserem Hosting-Anbieter in Deutschland
      </template>
      betrieben. Beim Aufruf jeder Seite verarbeitet der Webserver technisch notwendige Daten: IP-Adresse, Datum und Uhrzeit, aufgerufene Adresse, übertragene Datenmenge, Referrer sowie Browser- und Betriebssystemangaben. Diese Daten sind erforderlich, um die Website auszuliefern und ihre Sicherheit zu gewährleisten (Art. 6 Abs. 1 lit. f DSGVO). Die Logfiles werden nach spätestens 14 Tagen gelöscht, sofern sie nicht zur Aufklärung eines Sicherheitsvorfalls benötigt werden. Mit dem Hosting-Anbieter besteht ein Vertrag zur Auftragsverarbeitung (Art. 28 DSGVO).
    </p>

    <template v-if="hasSocial">
      <h2>Links zu sozialen Netzwerken</h2>
      <p>
        Links zu unseren Profilen bei sozialen Netzwerken sind einfache Links. Daten werden erst an den jeweiligen Anbieter übertragen, wenn Sie einen Link anklicken.
      </p>
    </template>

    <template v-if="plausibleHost">
      <h2>Reichweitenmessung mit Plausible Analytics</h2>
      <p>
        Wir nutzen Plausible Analytics, das wir auf einem eigenen Server betreiben ({{ plausibleHost }}). Plausible setzt keine Cookies und speichert nichts auf Ihrem Gerät. Ihre IP-Adresse wird nur kurzzeitig verarbeitet, um zusammen mit einem täglich wechselnden geheimen Schlüssel einen anonymen Besuchszähler zu bilden; sie wird nicht gespeichert. Wir sehen ausschließlich zusammengefasste Statistiken wie Seitenaufrufe, Herkunftsseiten, Länder und Gerätetypen. Rechtsgrundlage ist unser berechtigtes Interesse an einer datensparsamen Auswertung der Nutzung unserer Website (Art. 6 Abs. 1 lit. f DSGVO). Da kein Zugriff auf Ihr Endgerät erfolgt, ist keine Einwilligung nach § 25 TDDDG erforderlich. Die Daten verlassen unseren Server nicht.
      </p>
    </template>

    <h2>Kontaktformular und E-Mail</h2>
    <p>
      Wenn Sie uns über das Formular oder per E-Mail schreiben, verarbeiten wir Ihren Namen, Ihre E-Mail-Adresse, optional Ihr Unternehmen und Ihre Nachricht, um Ihre Anfrage zu beantworten. Beim Kontaktformular erfolgt die Verarbeitung auf Grundlage Ihrer Einwilligung (Art. 6 Abs. 1 lit. a DSGVO). Sie können diese jederzeit mit Wirkung für die Zukunft per E-Mail an {{ company.email }} widerrufen; die Rechtmäßigkeit der Verarbeitung bis zum Widerruf bleibt unberührt. Bei Anfragen per E-Mail ist die Rechtsgrundlage Art. 6 Abs. 1 lit. b DSGVO bei vorvertraglichen Anfragen, sonst lit. f. Die Nachricht wird per verschlüsselter Verbindung an unser E-Mail-Postfach übermittelt und nicht in einer Datenbank gespeichert. Die über das Formular erteilte Einwilligung wird mit dem Versandzeitpunkt in der Nachricht vermerkt. Wir löschen Anfragen, sobald sie erledigt sind und keine gesetzlichen Aufbewahrungspflichten bestehen.
    </p>
    <p>
      Zum Schutz vor Missbrauch setzt das Formular erst, wenn Sie hineinklicken, ein Sitzungs-Cookie (<code>dg_csrf</code>). Es enthält eine zufällige Kennung, mit der wir prüfen, dass die Nachricht tatsächlich von diesem Formular stammt (CSRF-Schutz), und wird beim Schließen des Browsers gelöscht. Dieses Cookie ist für den von Ihnen gewünschten Dienst unbedingt erforderlich (§ 25 Abs. 2 Nr. 2 TDDDG) und benötigt daher keine Einwilligung. Zusätzlich speichern wir für kurze Zeit einen nicht rückrechenbaren Hashwert Ihrer IP-Adresse, um massenhafte Anfragen zu begrenzen.
    </p>

    <h2>Ihre Rechte</h2>
    <p>Sie haben das Recht auf</p>
    <ul>
      <li>Auskunft über Ihre gespeicherten Daten (Art. 15 DSGVO),</li>
      <li>Berichtigung (Art. 16 DSGVO) und Löschung (Art. 17 DSGVO),</li>
      <li>Einschränkung der Verarbeitung (Art. 18 DSGVO),</li>
      <li>Datenübertragbarkeit (Art. 20 DSGVO),</li>
      <li>Widerspruch gegen Verarbeitungen auf Grundlage berechtigter Interessen (Art. 21 DSGVO).</li>
    </ul>
    <p>
      Wenden Sie sich dafür formlos an <a :href="`mailto:${company.email}`">{{ company.email }}</a>. Außerdem können Sie sich bei einer Datenschutz-Aufsichtsbehörde beschweren, etwa beim Landesbeauftragten für den Datenschutz und die Informationsfreiheit Baden-Württemberg.
    </p>

    <h2>Sicherheit</h2>
    <p>Die gesamte Website wird ausschließlich verschlüsselt über HTTPS ausgeliefert.</p>
  </div>
</template>
